export const privacyContent = `
<div class="space-y-8">
  <!-- 1. Who We Are -->
  <section>
    <h2 class="text-2xl font-semibold mb-4">Who We Are</h2>
    <p class="text-gray-300 leading-relaxed">
      We are <strong>OXO Play</strong> (“<em>OXO Play</em>,” “<em>we</em>,” “<em>us</em>”). We offer an online gaming platform where you can discover, play, and enjoy various web-based games in a convenient and accessible manner (the “<em>Platform</em>”). You can access our Platform with or without registering an account (the “<em>Account</em>”). The Platform is accessible on our official website, which may vary depending on your region (the “<em>Website</em>”).
    </p>
    <p class="text-gray-300 leading-relaxed mt-4">
      We understand the responsibility you entrust in us and take your privacy seriously. This Privacy Policy (this “<em>Policy</em>”) explains how we collect, process, disclose, retain, and protect your personal data when you use our Platform. Please read this Policy carefully to understand your rights and our obligations.
    </p>
    <p class="text-gray-300 leading-relaxed mt-4">
      The Platform is intended for visitors and users who are at least thirteen (13) years of age (or the applicable minimum age in your country). If you are under thirteen (13) (or the applicable minimum age in your jurisdiction), the Platform is not intended for you, and we request that you do not use it. We do not knowingly collect or solicit personal information from anyone under the applicable minimum age.
    </p>
    <p class="text-gray-300 leading-relaxed mt-4">
      For information about the terms under which we operate, please also review our Terms of Service.
    </p>
  </section>

  <!-- 2. What is Personal Data? -->
  <section>
    <h2 class="text-2xl font-semibold mb-4">What is Personal Data?</h2>
    <p class="text-gray-300 leading-relaxed">
      “<strong>Personal Data</strong>” is information about you that allows us to identify you directly or indirectly. Examples include your name or email address. It may also include data about the games you have played if we can connect this information to your Account on our Platform.
    </p>
    <p class="text-gray-300 leading-relaxed mt-4">
      We strongly recommend that you avoid using your real name and sharing personal data within any games offered on our Platform, particularly in multiplayer contexts.
    </p>
  </section>

  <!-- 3. From Whom Do We Collect Personal Data? -->
  <section>
    <h2 class="text-2xl font-semibold mb-4">From Whom Do We Collect Personal Data?</h2>
    <p class="text-gray-300 leading-relaxed">
      In order to operate our Platform, we may collect data from various sources:
    </p>
    <ul class="list-disc list-inside mt-4 text-gray-300 space-y-2">
      <li>Visitors and users of the Platform and Website</li>
      <li>Developers of web games</li>
      <li>Individuals who provide us with their contact details</li>
      <li>Anyone communicating with us by email, support tickets, or other means</li>
    </ul>
    <p class="text-gray-300 leading-relaxed mt-4">
      As noted above, we only collect personal data from users who are at least thirteen (13) years of age or the applicable minimum age in their jurisdiction.
    </p>
  </section>

  <!-- 4. What Personal Data Does OXO Play Process and Why? -->
  <section>
    <h2 class="text-2xl font-semibold mb-4">What Personal Data Does OXO Play Process and Why?</h2>
    <p class="text-gray-300 leading-relaxed">
      OXO Play may collect and process the following categories of personal data, based on the reasons (“why”) and lawful bases (“on what basis”) outlined below:
    </p>
    <div class="overflow-auto mt-4">
      <table class="min-w-full text-left text-gray-300 border-collapse">
        <thead>
          <tr>
            <th class="border-b py-2 pr-4 font-semibold">Purpose</th>
            <th class="border-b py-2 pr-4 font-semibold">Personal Data</th>
            <th class="border-b py-2 font-semibold">Legal Basis</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="border-b py-2 pr-4 align-top">
              Evaluate job applications
            </td>
            <td class="border-b py-2 pr-4 align-top">
              Email address, name, age, gender, CV
            </td>
            <td class="border-b py-2 align-top">
              Necessary for the performance of a contract or to take steps before entering into a contract
            </td>
          </tr>
          <tr>
            <td class="border-b py-2 pr-4 align-top">
              Register and authenticate your Account
            </td>
            <td class="border-b py-2 pr-4 align-top">
              Email address, name, age, gender, external account info, username, password
            </td>
            <td class="border-b py-2 align-top">
              Prior, express, free, specific, and informed consent of the user
            </td>
          </tr>
          <tr>
            <td class="border-b py-2 pr-4 align-top">
              Personalize your gaming experience (Account)
            </td>
            <td class="border-b py-2 pr-4 align-top">
              Username, device/connection data, selected interests, gaming behavior, feedback
            </td>
            <td class="border-b py-2 align-top">
              Prior, express, free, specific, and informed consent of the user
            </td>
          </tr>
          <tr>
            <td class="border-b py-2 pr-4 align-top">
              Respond to inquiries or complaints
            </td>
            <td class="border-b py-2 pr-4 align-top">
              Name, email address, and any other info you provide
            </td>
            <td class="border-b py-2 align-top">
              Necessary for our legitimate interests, specifically improving quality and support
            </td>
          </tr>
          <tr>
            <td class="border-b py-2 pr-4 align-top">
              Inform you of new Platform functionalities
            </td>
            <td class="border-b py-2 pr-4 align-top">
              Username, email address
            </td>
            <td class="border-b py-2 align-top">
              Necessary for our legitimate interests, specifically to communicate relevant updates
            </td>
          </tr>
          <tr>
            <td class="border-b py-2 pr-4 align-top">
              Analyze usage statistics to improve the Platform
            </td>
            <td class="border-b py-2 pr-4 align-top">
              Data on user behavior (usage &amp; device data), in-game statistics
            </td>
            <td class="border-b py-2 align-top">
              Necessary for our legitimate interests in understanding Platform performance and user experience
            </td>
          </tr>
          <tr>
            <td class="border-b py-2 pr-4 align-top">
              Comply with legal obligations
            </td>
            <td class="border-b py-2 pr-4 align-top">
              Data required by applicable law
            </td>
            <td class="border-b py-2 align-top">
              Necessary for compliance with a legal obligation
            </td>
          </tr>
          <tr>
            <td class="border-b py-2 pr-4 align-top">
              Prevent, detect, and combat fraud/unauthorized activities
            </td>
            <td class="border-b py-2 pr-4 align-top">
              Data required for fraud or illegal activity detection
            </td>
            <td class="border-b py-2 align-top">
              Necessary for our legitimate interests, specifically preventing fraud and illegal activities
            </td>
          </tr>
          <tr>
            <td class="border-b py-2 pr-4 align-top">
              Marketing communications
            </td>
            <td class="border-b py-2 pr-4 align-top">
              Username, email address
            </td>
            <td class="border-b py-2 align-top">
              Prior, express, free, specific, and informed consent of the user
            </td>
          </tr>
          <tr>
            <td class="border-b py-2 pr-4 align-top">
              Enable advertising on our Platform
            </td>
            <td class="border-b py-2 pr-4 align-top">
              Data gathered via cookies or similar technologies (IP address, user IDs, browser type)
            </td>
            <td class="border-b py-2 align-top">
              Prior, express, free, specific, and informed consent, when accepting our Privacy &amp; Cookie Policy and confirming ad preferences
            </td>
          </tr>
          <tr>
            <td class="border-b py-2 pr-4 align-top">
              Fulfill obligations to game developers
            </td>
            <td class="border-b py-2 pr-4 align-top">
              Email, tax ID, bank account identification, payment details
            </td>
            <td class="border-b py-2 align-top">
              Necessary for the performance of a contract
            </td>
          </tr>
          <tr>
            <td class="border-b py-2 pr-4 align-top">
              Facilitate in-game purchases
            </td>
            <td class="border-b py-2 pr-4 align-top">
              Data related to the purchase (processed by third-party providers)
            </td>
            <td class="border-b py-2 align-top">
              Necessary for the performance of a contract
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="text-gray-300 leading-relaxed mt-4">
      <strong>Registration of Account:</strong> By registering an Account, you consent to provide a password, email address, user agent, and IP address. We may facilitate logins via third-party providers (e.g., Facebook, Google). If you use such an option, you allow that provider to share your user details (e.g., name, email address, date of birth) with us.
    </p>
    <p class="text-gray-300 leading-relaxed mt-4">
      <strong>Advertising:</strong> We may display ads from various advertisers on the Platform. These may be personalized or non-personalized. Personalized ads require your consent, where we share certain data (e.g., IP address, ad identifiers) with third-party advertisers, who in turn provide targeted ads. You can adjust your ad preferences in our Cookie Management tool or your browser settings.
    </p>
    <p class="text-gray-300 leading-relaxed mt-4">
      <strong>Profiling:</strong> We may analyze your gameplay data (e.g., in-game stats) to make recommendations. This is not considered “automated decision-making” that significantly affects you without a lawful basis. If you are located in certain regions (like the EEA or UK), you may have the right to object to direct marketing or profiling based on your data.
    </p>
  </section>

  <!-- 5. Do We Share Your Personal Data With Others? -->
  <section>
    <h2 class="text-2xl font-semibold mb-4">Do We Share Your Personal Data with Others?</h2>
    <p class="text-gray-300 leading-relaxed">
      We may share your personal data with the following entities:
    </p>
    <ul class="list-disc list-inside mt-4 text-gray-300 space-y-2">
      <li>
        <strong>Service Providers:</strong> We may share your personal data with third-party providers who assist in our operations, such as IT services, payment processors, or data analysis.
      </li>
      <li>
        <strong>Users:</strong> Other users may see certain basic information if you opt to have a public or semi-public profile (e.g., username, game stats). You can adjust your visibility in your Account settings.
      </li>
      <li>
        <strong>Advertising:</strong> We sometimes share hashed or anonymized data (e.g., hashed email, IP address) with advertisers or ad networks to enable personalized advertising, but only with your consent.
      </li>
      <li>
        <strong>Game Developers:</strong> Some games on our Platform are developed by external partners. We may share usage data (in anonymized form) or certain purchase-related data with these developers.
      </li>
      <li>
        <strong>Payment Providers:</strong> If you make in-game purchases, the transaction is processed by a third-party provider. We do not collect your full payment details but link purchase info to your Account.
      </li>
      <li>
        <strong>International Data Transfers:</strong> Your data may be transferred outside your home jurisdiction. In such cases, we implement appropriate safeguards (e.g., Standard Contractual Clauses).
      </li>
    </ul>
  </section>

  <!-- 6. How Long Do We Keep Your Personal Data? -->
  <section>
    <h2 class="text-2xl font-semibold mb-4">How Long Do We Keep Your Personal Data?</h2>
    <p class="text-gray-300 leading-relaxed">
      We store your personal data only as long as it is necessary to fulfill the purposes outlined in this Policy. If you have an Account, we will retain your information until you request deletion or for as long as needed for legitimate legal or business purposes. You can delete your Account at any time, though deletion is permanent and cannot be undone.
    </p>
    <p class="text-gray-300 leading-relaxed mt-4">
      In some cases, we may keep your data longer if it is required by law, necessary to resolve disputes, or to protect against fraud.
    </p>
  </section>

  <!-- 7. Is Your Data Safe with OXO Play? -->
  <section>
    <h2 class="text-2xl font-semibold mb-4">Is Your Data Safe with OXO Play?</h2>
    <p class="text-gray-300 leading-relaxed">
      We take appropriate technical and organizational measures to protect your personal data against loss, theft, or unauthorized access. Access to your personal data is limited to employees, contractors, or agents who need it to perform their job responsibilities. If we engage any third-party processors, we require them to adhere to strict data protection standards.
    </p>
  </section>

  <!-- 8. You Are in Control of Your Data -->
  <section>
    <h2 class="text-2xl font-semibold mb-4">You Are in Control of Your Data</h2>
    <p class="text-gray-300 leading-relaxed">
      Depending on your jurisdiction, you may have certain rights regarding your personal data:
    </p>
    <ul class="list-disc list-inside mt-4 text-gray-300 space-y-2">
      <li><strong>Access and Copy:</strong> Request a copy of your personal data.</li>
      <li><strong>Rectification:</strong> Correct inaccuracies in your personal data.</li>
      <li><strong>Deletion (Right to be Forgotten):</strong> Request deletion of your personal data if there is no valid reason for us to keep it.</li>
      <li><strong>Restriction:</strong> Temporarily limit our processing of your data.</li>
      <li><strong>Withdraw Consent:</strong> Withdraw any consent you have given us.</li>
      <li><strong>Object to Marketing/Profiling:</strong> Opt out of receiving direct marketing or profiling based on your data.</li>
      <li><strong>Data Portability:</strong> Request a copy of your data in a structured, commonly used, and machine-readable format.</li>
      <li><strong>Non-Discrimination:</strong> We will not discriminate against you for exercising your rights.</li>
    </ul>
    <p class="text-gray-300 leading-relaxed mt-4">
      To exercise any of these rights, please contact us via the methods provided on our Website. We may request proof of identity before acting on your request. We will respond within a reasonable timeframe and in compliance with applicable laws.
    </p>
  </section>

  <!-- 9. Manage Your Ad Preferences -->
  <section>
    <h2 class="text-2xl font-semibold mb-4">Manage Your Ad Preferences</h2>
    <p class="text-gray-300 leading-relaxed">
      When you first visit our Website, you may be prompted to accept or reject certain cookies via our Consent Management Platform (CMP). You can change these preferences at any time in your Account settings or via the “Preferences” link. You can also manage cookies at the browser level. Blocking cookies may limit some functionalities of our Platform.
    </p>
    <p class="text-gray-300 leading-relaxed mt-4">
      For more information about online behavioral advertising and your choices, please consult the resources provided by relevant advertising authorities or organizations.
    </p>
  </section>

  <!-- 10. Privacy of Children -->
  <section>
    <h2 class="text-2xl font-semibold mb-4">Privacy of Children</h2>
    <p class="text-gray-300 leading-relaxed">
      Our Platform is intended for users thirteen (13) years of age or older (or the applicable minimum age in your country). We do not knowingly collect personal data from children under thirteen (13). If you believe we have inadvertently collected such data, please notify us immediately, and we will take steps to delete the information.
    </p>
  </section>

  <!-- 11. Links to Other Websites -->
  <section>
    <h2 class="text-2xl font-semibold mb-4">Links to Other Websites</h2>
    <p class="text-gray-300 leading-relaxed">
      Our Platform or individual games may contain links to external sites not operated by us. We are not responsible for the privacy practices of these third-party websites or services. We encourage you to review their privacy policies before providing them with your data.
    </p>
  </section>

  <!-- 12. Externally Hosted Games -->
  <section>
    <h2 class="text-2xl font-semibold mb-4">Externally Hosted Games</h2>
    <p class="text-gray-300 leading-relaxed">
      This Policy does not apply to the processing of personal data by data controllers other than OXO Play, such as external developers hosting games. While we require these developers to comply with applicable data protection laws, you should review their individual privacy statements before playing any third-party game.
    </p>
  </section>

  <!-- 13. United States Privacy Rights -->
  <section>
    <h2 class="text-2xl font-semibold mb-4">United States Privacy Rights</h2>
    <p class="text-gray-300 leading-relaxed">
      Residents of certain U.S. states have specific rights regarding their personal information, such as the right to access, delete, or opt out of the “sale” or “sharing” of personal data. Where applicable, we will comply with these laws. If you wish to exercise any U.S. state-specific privacy rights, please contact us.
    </p>
    <p class="text-gray-300 leading-relaxed mt-4">
      We do not discriminate against individuals for exercising their rights under applicable law.
    </p>
  </section>

  <!-- 14. Changes to Our Privacy Policy -->
  <section>
    <h2 class="text-2xl font-semibold mb-4">Changes to Our Privacy Policy</h2>
    <p class="text-gray-300 leading-relaxed">
      We may update this Policy from time to time and will indicate the “Last updated” date at the top. For significant changes, we will also notify you via email or other prominent notice on our Platform. By continuing to use our Platform after such changes, you agree to the updated terms.
    </p>
  </section>

  <!-- 15. More Questions About Your Data? -->
  <section>
    <h2 class="text-2xl font-semibold mb-4">More Questions About Your Data?</h2>
    <p class="text-gray-300 leading-relaxed">
      If you have any questions or concerns about this Privacy Policy or our handling of your personal data, please reach out to us through our 
      <a href="/contact" class="underline hover:no-underline">Contact Us</a> 
      page or via the email address provided there. 
      If you believe we have not adequately addressed your concerns, you have the right to lodge a complaint with a relevant data protection authority.
    </p>
  </section>

</div>
`; 